import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconPencil, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { formatDateOnly } from "@how/ui";
import { useCampuses } from "../campuses/campus-queries";
import {
  useRemoveContact,
  useSetContact,
  useSetCustomValue,
  useSetPreference,
  useUpdatePerson,
  type CustomValue,
  type PersonDetail,
  type PersonFields,
} from "./queries";

const saveFailed = (
  <Text size="sm" c="red">
    Not saved. Check your access and try again.
  </Text>
);

// ------------------------------------------------------------------------------------------ the person

/** Name and birthdate. Dates use the browser's own date input, so there is no date-picker dependency to approve. */
export function PersonDetailsForm({
  person,
  onDone,
}: {
  readonly person: PersonDetail["person"];
  readonly onDone: () => void;
}) {
  const update = useUpdatePerson();
  const campuses = useCampuses();
  const [fields, setFields] = useState<PersonFields>({
    firstName: person.first_name,
    lastName: person.last_name,
    preferredName: person.preferred_name ?? "",
    birthdate: person.birthdate ?? "",
    primaryCampusId: person.primary_campus_id ?? "",
  });
  const set = (key: keyof PersonFields) => (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    setFields((current) => ({ ...current, [key]: value }));
  };
  const valid = fields.firstName.trim().length > 0 && fields.lastName.trim().length > 0;

  return (
    <Stack gap="sm" mt="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label="First name"
          required
          value={fields.firstName}
          onChange={set("firstName")}
        />
        <TextInput label="Last name" required value={fields.lastName} onChange={set("lastName")} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label="Goes by"
          description="Only when it differs from the first name."
          value={fields.preferredName}
          onChange={set("preferredName")}
        />
        <TextInput
          label="Birthdate"
          type="date"
          value={fields.birthdate}
          onChange={set("birthdate")}
        />
      </SimpleGrid>
      {/* A single-campus church sees one option and its "no campus" state; the question still gets asked once. */}
      <Select
        label="Home campus"
        description="Where this person usually attends. Leave empty if it is not known."
        placeholder="No campus"
        clearable
        searchable
        data={(campuses.data ?? [])
          .filter((c) => c.isActive || c.id === fields.primaryCampusId)
          .map((c) => ({ value: c.id, label: c.isActive ? c.name : `${c.name} (archived)` }))}
        value={fields.primaryCampusId.length === 0 ? null : fields.primaryCampusId}
        onChange={(value) => {
          setFields((current) => ({ ...current, primaryCampusId: value ?? "" }));
        }}
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={!valid}
          loading={update.isPending}
          onClick={() => {
            update.mutate({ personId: person.id, fields }, { onSuccess: onDone });
          }}
        >
          Save
        </Button>
      </Group>
      {update.isError ? saveFailed : null}
    </Stack>
  );
}

// ------------------------------------------------------------------------------------------ contacts

export function ContactsSection({
  personId,
  contacts,
  canWrite,
}: {
  readonly personId: string;
  readonly contacts: PersonDetail["contacts"];
  readonly canWrite: boolean;
}) {
  const setContact = useSetContact();
  const removeContact = useRemoveContact();
  const [adding, setAdding] = useState(false);
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [primary, setPrimary] = useState(contacts.length === 0);

  return (
    <section>
      <Group justify="space-between">
        <Title order={4}>Contact</Title>
        {canWrite && !adding ? (
          <Button
            variant="subtle"
            size="xs"
            aria-label="Add a contact method"
            onClick={() => {
              setAdding(true);
            }}
          >
            Add
          </Button>
        ) : null}
      </Group>
      {contacts.length === 0 ? (
        <Text size="sm" c="dimmed">
          No email or phone recorded.
        </Text>
      ) : (
        <Stack gap={4} mt="xs">
          {contacts.map((contact) => (
            <Group key={contact.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text size="sm">{contact.value}</Text>
                <Text size="xs" c="dimmed">
                  {contact.label ?? contact.method}
                  {contact.is_primary ? ", primary" : ""}
                </Text>
              </Group>
              {canWrite ? (
                <Tooltip label={`Remove ${contact.value}`}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Remove ${contact.value}`}
                    loading={
                      removeContact.isPending && removeContact.variables.contactId === contact.id
                    }
                    onClick={() => {
                      removeContact.mutate({ personId, contactId: contact.id });
                    }}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          ))}
        </Stack>
      )}
      {adding ? (
        <Stack gap="xs" mt="sm">
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Type"
              w={110}
              data={[
                { value: "email", label: "Email" },
                { value: "phone", label: "Phone" },
              ]}
              value={method}
              allowDeselect={false}
              onChange={(next) => {
                if (next === "email" || next === "phone") setMethod(next);
              }}
            />
            <TextInput
              label={method === "email" ? "Email address" : "Phone number"}
              style={{ flex: 1, minWidth: 180 }}
              value={value}
              onChange={(event) => {
                setValue(event.currentTarget.value);
              }}
            />
            <TextInput
              label="Label"
              placeholder="home, work"
              w={120}
              value={label}
              onChange={(event) => {
                setLabel(event.currentTarget.value);
              }}
            />
          </Group>
          <Checkbox
            label="Primary for this type"
            checked={primary}
            onChange={(event) => {
              setPrimary(event.currentTarget.checked);
            }}
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={value.trim().length === 0}
              loading={setContact.isPending}
              onClick={() => {
                setContact.mutate(
                  { personId, method, value, label, primary },
                  {
                    onSuccess: () => {
                      setAdding(false);
                      setValue("");
                      setLabel("");
                    },
                  },
                );
              }}
            >
              Add contact
            </Button>
          </Group>
          {setContact.isError ? (
            <Text size="sm" c="red">
              Not saved. Check that the {method === "email" ? "address" : "number"} is written
              correctly.
            </Text>
          ) : null}
        </Stack>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------------------------- custom fields

type Field = PersonDetail["fields"][number];

/** A field's current value as text, the way the record shows it when not editing. */
function displayValue(field: Field): string | null {
  const value = field.value;
  if (value === null) return null;
  switch (field.definition.field_type) {
    case "date":
      return formatDateOnly(value.value_date);
    case "boolean":
      return value.value_boolean === true ? "Yes" : "No";
    case "number":
      return value.value_number === null ? null : String(value.value_number);
    case "multi_select":
      return (value.value_options ?? []).join(", ");
    default:
      return value.value_text;
  }
}

/** The editor's draft value for a field, as the input for that type holds it. */
type Draft = string | number | boolean | string[] | null;

function draftOf(field: Field): Draft {
  const value = field.value;
  switch (field.definition.field_type) {
    case "date":
      return value?.value_date ?? "";
    case "boolean":
      return value?.value_boolean ?? false;
    case "number":
      return value?.value_number ?? null;
    case "multi_select":
      return value?.value_options ?? [];
    default:
      return value?.value_text ?? "";
  }
}

/** Turns a draft back into the one argument the write function takes, or nothing to clear the field. */
function toCustomValue(field: Field, draft: Draft): CustomValue {
  switch (field.definition.field_type) {
    case "date":
      return typeof draft === "string" && draft.length > 0 ? { p_date: draft } : {};
    case "boolean":
      return { p_boolean: draft === true };
    case "number":
      return typeof draft === "number" ? { p_number: draft } : {};
    case "multi_select":
      return Array.isArray(draft) && draft.length > 0 ? { p_options: draft } : {};
    default:
      return typeof draft === "string" && draft.trim().length > 0 ? { p_text: draft.trim() } : {};
  }
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  readonly field: Field;
  readonly value: Draft;
  readonly onChange: (value: Draft) => void;
}) {
  const { label, field_type: type, options } = field.definition;
  const description = field.definition.help_text ?? undefined;
  const common = { label, ...(description === undefined ? {} : { description }) };
  switch (type) {
    case "long_text":
      return (
        <Textarea
          {...common}
          autosize
          minRows={2}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      );
    case "number":
      return (
        <NumberInput
          {...common}
          value={typeof value === "number" ? value : ""}
          onChange={(next) => {
            onChange(typeof next === "number" ? next : null);
          }}
        />
      );
    case "date":
      return (
        <TextInput
          {...common}
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      );
    case "boolean":
      return (
        <Switch
          {...common}
          checked={value === true}
          onChange={(event) => {
            onChange(event.currentTarget.checked);
          }}
        />
      );
    case "select":
      return (
        <Select
          {...common}
          clearable
          data={options}
          value={typeof value === "string" && value.length > 0 ? value : null}
          onChange={(next) => {
            onChange(next ?? "");
          }}
        />
      );
    case "multi_select":
      return (
        <MultiSelect
          {...common}
          data={options}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
        />
      );
    default:
      return (
        <TextInput
          {...common}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      );
  }
}

/**
 * The church's own fields. Read-only by default and edited all at once, so the record reads as a record and not as
 * a form; only fields whose value actually changed are written.
 */
export function CustomFieldsSection({
  personId,
  fields,
  canWrite,
}: {
  readonly personId: string;
  readonly fields: PersonDetail["fields"];
  readonly canWrite: boolean;
}) {
  const setValue = useSetCustomValue();
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  if (fields.length === 0) return null;

  const start = () => {
    setDrafts(Object.fromEntries(fields.map((field) => [field.definition.id, draftOf(field)])));
    setFailed(false);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setFailed(false);
    try {
      for (const field of fields) {
        const draft = drafts[field.definition.id] ?? null;
        if (JSON.stringify(draft) === JSON.stringify(draftOf(field))) continue;
        await setValue.mutateAsync({
          personId,
          definitionId: field.definition.id,
          value: toCustomValue(field, draft),
        });
      }
      setEditing(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <Group justify="space-between">
        <Title order={4}>Details</Title>
        {canWrite && !editing ? (
          <Button
            variant="subtle"
            size="xs"
            aria-label="Edit details"
            leftSection={<IconPencil size={14} />}
            onClick={start}
          >
            Edit
          </Button>
        ) : null}
      </Group>
      {editing ? (
        <Stack gap="sm" mt="xs">
          {fields.map((field) => (
            <FieldInput
              key={field.definition.id}
              field={field}
              value={drafts[field.definition.id] ?? null}
              onChange={(value) => {
                setDrafts((current) => ({ ...current, [field.definition.id]: value }));
              }}
            />
          ))}
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button loading={saving} onClick={() => void save()}>
              Save details
            </Button>
          </Group>
          {failed ? saveFailed : null}
        </Stack>
      ) : (
        <Stack gap={4} mt="xs">
          {fields.map((field) => {
            const shown = displayValue(field);
            return (
              <Group key={field.definition.id} gap="xs" wrap="nowrap" align="baseline">
                <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                  {field.definition.label}
                </Text>
                <Text size="sm" c={shown === null || shown.length === 0 ? "dimmed" : "bright"}>
                  {shown === null || shown.length === 0 ? "Not recorded" : shown}
                </Text>
              </Group>
            );
          })}
        </Stack>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------- preferences

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "Text messages" },
  { value: "phone", label: "Phone calls" },
  { value: "mail", label: "Postal mail" },
] as const;

/**
 * Consent, per channel. Three states rather than a switch's two, because "never asked" and "said no" are different
 * facts and the messaging module will treat them differently: a switch would record a refusal nobody gave.
 */
export function PreferencesSection({
  personId,
  preferences,
  canManage,
}: {
  readonly personId: string;
  readonly preferences: PersonDetail["preferences"];
  readonly canManage: boolean;
}) {
  const setPreference = useSetPreference();
  return (
    <section>
      <Title order={4}>Communication</Title>
      <Stack gap="xs" mt="xs">
        {CHANNELS.map((channel) => {
          const recorded = preferences.find((p) => p.channel === channel.value);
          const pending =
            setPreference.isPending && setPreference.variables.channel === channel.value;
          return (
            <Group key={channel.value} justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={0}>
                <Text size="sm">{channel.label}</Text>
                <Text size="xs" c="dimmed">
                  {recorded === undefined
                    ? "Not asked"
                    : `${recorded.opted_in ? "Opted in" : "Opted out"} on ${new Date(recorded.recorded_at).toLocaleDateString()}, ${recorded.source}`}
                </Text>
              </Stack>
              {canManage ? (
                <Group gap={4} wrap="nowrap">
                  <Button
                    size="xs"
                    variant={recorded?.opted_in === true ? "light" : "subtle"}
                    color="teal"
                    disabled={recorded?.opted_in === true}
                    loading={pending && setPreference.variables.optedIn}
                    onClick={() => {
                      setPreference.mutate({ personId, channel: channel.value, optedIn: true });
                    }}
                  >
                    Opt in
                  </Button>
                  <Button
                    size="xs"
                    variant={recorded?.opted_in === false ? "light" : "subtle"}
                    color="gray"
                    disabled={recorded?.opted_in === false}
                    loading={pending && !setPreference.variables.optedIn}
                    onClick={() => {
                      setPreference.mutate({ personId, channel: channel.value, optedIn: false });
                    }}
                  >
                    Opt out
                  </Button>
                </Group>
              ) : null}
            </Group>
          );
        })}
      </Stack>
      {setPreference.isError ? saveFailed : null}
    </section>
  );
}
